import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  MaintenanceStatusBadge,
  MaintenanceTypeBadge,
} from '@/modules/maintenance/components';
import { useMaintenanceTasks } from '@/modules/maintenance/hooks';
import { filterMaintenanceTasks } from '@/modules/maintenance/services/maintenanceRepository';
import {
  MAINTENANCE_STATUS_LABELS,
  MAINTENANCE_TYPE_LABELS,
  type MaintenanceStatus,
  type MaintenanceType,
} from '@/modules/maintenance/types/maintenance';
import { modulePaths } from '@/routes/paths';
import { useAuth } from '@/shared/hooks/useAuth';

const STATUS_OPTIONS: Array<MaintenanceStatus | 'all'> = [
  'all',
  'scheduled',
  'in_progress',
  'completed',
  'cancelled',
];

const TYPE_OPTIONS: Array<MaintenanceType | 'all'> = [
  'all',
  'preventive',
  'repair',
  'inspection',
  'cleaning',
];

export function MaintenanceListPage() {
  const { tasks, loading, error } = useMaintenanceTasks();
  const { hasPermission } = useAuth();
  const canWrite = hasPermission('maintenance.write');

  const [statusFilter, setStatusFilter] = useState<MaintenanceStatus | 'all'>(
    'all',
  );
  const [typeFilter, setTypeFilter] = useState<MaintenanceType | 'all'>('all');
  const [itemId, setItemId] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [search, setSearch] = useState('');

  const filtered = useMemo(
    () =>
      filterMaintenanceTasks(tasks, {
        status: statusFilter,
        type: typeFilter,
        ...(itemId ? { inventoryItemId: itemId } : {}),
        ...(dateFrom ? { fromDate: dateFrom } : {}),
        ...(dateTo ? { toDate: dateTo } : {}),
        search,
      }),
    [tasks, statusFilter, typeFilter, itemId, dateFrom, dateTo, search],
  );

  return (
    <section>
      <div className="inventory-page-header">
        <h1>Mantenimiento</h1>
        {canWrite ? (
          <Link
            to={`${modulePaths.maintenance}/nueva`}
            className="auth-button inventory-page-header__btn"
          >
            Nueva tarea
          </Link>
        ) : null}
      </div>

      <div className="reservation-filters users-form-grid">
        <label className="auth-label">
          Estado
          <select
            value={statusFilter}
            onChange={(e) =>
              setStatusFilter(e.target.value as MaintenanceStatus | 'all')
            }
          >
            {STATUS_OPTIONS.map((s) => (
              <option key={s} value={s}>
                {s === 'all' ? 'Todos' : MAINTENANCE_STATUS_LABELS[s]}
              </option>
            ))}
          </select>
        </label>
        <label className="auth-label">
          Tipo
          <select
            value={typeFilter}
            onChange={(e) =>
              setTypeFilter(e.target.value as MaintenanceType | 'all')
            }
          >
            {TYPE_OPTIONS.map((t) => (
              <option key={t} value={t}>
                {t === 'all' ? 'Todos' : MAINTENANCE_TYPE_LABELS[t]}
              </option>
            ))}
          </select>
        </label>
        <label className="auth-label">
          ID artículo
          <input
            value={itemId}
            onChange={(e) => setItemId(e.target.value)}
            placeholder="inventoryItemId"
          />
        </label>
        <label className="auth-label">
          Desde
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
          />
        </label>
        <label className="auth-label">
          Hasta
          <input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
          />
        </label>
        <label className="auth-label">
          Buscar
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Título o notas"
          />
        </label>
      </div>

      {loading ? <p className="auth-muted">Cargando…</p> : null}
      {error ? (
        <p className="auth-error" role="alert">
          {error}
        </p>
      ) : null}

      <table className="inventory-table">
        <thead>
          <tr>
            <th>Título</th>
            <th>Tipo</th>
            <th>Estado</th>
            <th>Inicio</th>
            <th>Fin</th>
            <th>Artículo</th>
          </tr>
        </thead>
        <tbody>
          {filtered.map((t) => (
            <tr key={t.id}>
              <td>
                <Link to={`${modulePaths.maintenance}/${t.id}`}>{t.title}</Link>
              </td>
              <td>
                <MaintenanceTypeBadge type={t.type} />
              </td>
              <td>
                <MaintenanceStatusBadge status={t.status} />
              </td>
              <td>{t.scheduledStart}</td>
              <td>{t.scheduledEnd}</td>
              <td>
                <Link to={`${modulePaths.inventory}/${t.inventoryItemId}`}>
                  {t.inventoryItemId.slice(0, 8)}…
                </Link>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {!loading && filtered.length === 0 ? (
        <p className="auth-muted">Sin tareas para los filtros seleccionados.</p>
      ) : null}
    </section>
  );
}
